import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { removeNotification } from '../../store/slices/notificationSlice';
import { NotificationType } from '../../store/slices/notificationSlice';
import { RootState } from '../../store/store';
import checkIcon from '../../assets/icons/icon_Success.svg';
import errorIcon from '../../assets/icons/icon_Error.svg';
import warningIcon from '../../assets/icons/icon_Attention.svg';

interface NotificationProps {
  id: string;
  type: NotificationType;
  message: string;
  title?: string;
  autoHideDuration?: number;
}

const getIcon = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return <img src={checkIcon} alt="success" />;
    case 'error':
      return <img src={errorIcon} alt="error" />;
    case 'warning':
      return <img src={warningIcon} alt="warning" />;
    case 'info':
      return 'i';
    default:
      return '';
  }
};

const SingleNotification: React.FC<NotificationProps> = ({
  id,
  type,
  message,
  title,
  autoHideDuration = 5000,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isPaused, setIsPaused] = useState(false);

  // Remaining time and last resume timestamp persist across pause/resume cycles.
  const remainingRef = useRef<number>(autoHideDuration);
  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      remainingRef.current = Math.max(
        remainingRef.current - (Date.now() - startTimeRef.current),
        0,
      );
      return;
    }

    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      dispatch(removeNotification(id));
    }, remainingRef.current);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPaused, dispatch, id]);

  return (
    <div
      className={`notification notification--${type}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="notification__icon">{getIcon(type)}</div>
      <div className="notification__content">
        {title && <div className="notification__title">{title}</div>}
        <div className="notification__message">{message}</div>
      </div>
      <button
        className="notification__close"
        onClick={() => dispatch(removeNotification(id))}
        aria-label={t('notification_close')}
      >
        ✕
      </button>
      <div
        className={`notification__progress notification__progress--${type}`}
        style={{
          animationDuration: `${autoHideDuration}ms`,
          animationPlayState: isPaused ? 'paused' : 'running',
        }}
      />
    </div>
  );
};

const Notification: React.FC = () => {
  const notifications = useSelector((state: RootState) => state.notifications?.notifications || []);

  return (
    <div className="notification-container">
      {notifications.map((notification: NotificationProps) => (
        <SingleNotification key={notification.id} {...notification} />
      ))}
    </div>
  );
};

export default Notification; 